import React, { useState, useEffect } from "react";
import { HiMenuAlt3 } from "react-icons/hi";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import logo from '../assets/images/pam-web-logo.svg';
import HomeIcon from '../assets/icons/Home.svg';
import AddressIcon from '../assets/icons/Address.svg';
import SettingIcon from '../assets/icons/More.svg';
import FavouriteIcon from '../assets/images/favouriteheart.svg';
import Dashboard from '../assets/icons/Dashboard.svg';
import ManageUser from '../assets/icons/ManageUser.svg';
import DeleteUser from '../assets/icons/DeleteRequest.svg';
import Feedback from '../assets/icons/Feedback.svg';
import Report from '../assets/icons/Report.svg';
import {jwtDecode} from "jwt-decode"; // Correct import for jwt-decode

interface MenuItem {
  name: string;
  link: string;
  icon: string; 
}

interface TokenPayload {
  RoleId: number; // Use the correct casing as in the token payload
}

const SidebarLayout: React.FC = () => {
  const [roleId, setRoleId] = useState<number | null>(null);
  const [open, setOpen] = useState<boolean>(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode<TokenPayload>(token); // Correctly decode token
        setRoleId(Number(decodedToken.RoleId)); // Ensure RoleId is treated as a number
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, []);

  useEffect(() => {
  }, [roleId]);

  const fetchUserAddresses = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error('Token not found in localStorage');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/address/ViewAllAddedAddressByUserAsync', {
        method: 'GET',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user addresses: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // Handle the fetched data as needed
    } catch (error) {
      console.error('Error fetching user addresses:', error);
    }
  };
  useEffect(() => {
    const handleResize = () => {
      setOpen(window.innerWidth >= 768);
    };
  
    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    fetchUserAddresses();
  }, []);

  const menus: MenuItem[] = [
    { name: "Home", link: "/home", icon: HomeIcon }, 
    { name: "Address List", link: "/addressmapone", icon: AddressIcon },
    { name: "Favourites", link: "/favourites", icon: FavouriteIcon },
    { name: "More", link: "/setting", icon: SettingIcon },
    ...(roleId === 1 ? [
      { name: "Dashboard", link: "/dashboard", icon: Dashboard },
      { name: "Manage User", link: "/manageuser", icon: ManageUser },
      { name: "Delete Request", link: "/view-delete-request", icon: DeleteUser },
      { name: "Feedback", link: "/viewfeedback", icon: Feedback },
      { name: "Report", link: "/ViewreportIssue", icon: Report },
    ] : []),
  ];

  const getActiveMenu = (): string => {
    const path = location.pathname;
    
    // For Address related routes
    if (path.includes("/addressmapone") || path.includes("/add-new-address")) {
        return "/addressmapone";
    }
    
    // For Feedback related routes
    if (path.includes("/viewfeedback") || path.includes("/feedback-details")) {
        return "/viewfeedback";
    }
    
    // For Report Issue related routes
    if (path.includes("/view-report") || path.includes("/report-issue-details")) {
        return "/ViewreportIssue";
    }
    if (path.includes("/about") || path.includes("/feedback") || path.includes("/report-issue")) {
      return "/setting";
    }
    if (path.includes("/view-delete-request") || path.includes("/delete-request-details")) {
      return "/view-delete-request";
    }
  
    const firstSegment = "/" + (path.split("/")[1] || "home");
    return firstSegment;
  };
  
  const activeMenu = getActiveMenu();

  return (
    <section className="flex bg-slate-100 select-none" style={{ fontFamily: 'Poppins' }} > 
      <div
        className={`bg-white min-h-screen z-20 fixed transition-width duration-300 ${
          open ? "w-64" : "w-20"
        } px-4`}
      >
        <div className="flex items-center justify-between py-4 select-none">
          <h1
            className={`text-lg font-bold text-blue-600 ${
              !open && "hidden"
            } `}
          >
            <img src={logo} alt="Logo" />
          </h1>
          <HiMenuAlt3
            size={24}
            className="cursor-pointer text-blue-600"
            onClick={() => setOpen(!open)}
          />
        </div>

        <div className="mt-8 flex flex-col gap-3">
          {menus.map((menu, i) => (
            <Link
              to={menu.link}
              key={i}
              className={`flex items-center gap-4 px-4 py-2 rounded-md ${
                activeMenu === menu.link
                  ? "bg-blue-600 text-white"
                  : "text-black hover:bg-blue-600 hover:text-white"
              }`}
            >
              <div>
                <img
                  src={menu.icon}
                  alt={`${menu.name} Icon`}
                  className={`w-6 h-6 ${
                    activeMenu === menu.link ? "filter brightness-0 invert" : "text-blue-600"
                  }`}
                />
              </div>
              <h2
                className={`text-sm font-medium ${!open && "hidden"}`}
              >
                {menu.name}
              </h2>
            </Link>
          ))}
        </div>
      </div>

      <div className={`flex-1 p-0 mr-4 transition-margin duration-300 ${open ? "ml-64" : "ml-20"}`}>
        <Outlet /> 
      </div>
    </section>
  );
};

export default SidebarLayout;
